import { jsonFetch } from 'javascripts/fetch';
import { showAlertMessage } from 'javascripts/messages';
import { AhaEditorUtils, slate, type ToolArgs } from '@aha-app/aha-editor-v2';

export interface WhiteboardRequestOptions {
  baseURL: string;
  objectType?: string;
  objectId?: RecordId;
  getSharingToken?: () => string;
  templateId?: RecordId | null;
  isGlobalTemplate?: boolean;
  whiteboardId?: RecordId;
  llmToolCallId?: string | null;
}

export function insertEmbeddedWhiteboard({
  editor,
  currentElement = {},
  whiteboardId,
  placeholderId,
}) {
  const placeholderNode = AhaEditorUtils.Editor.firstElementWithProperties(
    editor,
    {
      placeholder_id: placeholderId,
    }
  );

  if (placeholderNode) {
    slate.Transforms.setNodes(
      editor,
      {
        ...currentElement,
        placeholder_id: undefined,
        ...embeddedWhiteboard(whiteboardId),
      },
      {
        at: placeholderNode[1],
      }
    );
  }
}

export async function duplicateWhiteboardRecord({
  baseURL,
  getSharingToken,
  objectId,
  objectType,
  whiteboardId,
}: WhiteboardRequestOptions) {
  const authorization = getSharingToken?.();

  const headers: HeadersInit = authorization
    ? { Authorization: `Bearer ${authorization}` }
    : {};

  if (authorization) {
    headers.Authorization = `Bearer ${authorization}`;
  }

  const response = await jsonFetch(
    `${baseURL}/api/v1/whiteboards/${whiteboardId}/duplicate`,
    {
      method: 'POST',
      body: JSON.stringify({
        whiteboardable_type: objectType,
        whiteboardable_id: objectId,
      }),
      headers,
    }
  );

  return response.json() as Promise<{ whiteboard: { id: `${number}` } }>;
}

export async function createWhiteboardRecord({
  baseURL,
  getSharingToken,
  isGlobalTemplate = false,
  objectId,
  objectType,
  templateId = null,
  llmToolCallId = null,
}: WhiteboardRequestOptions) {
  const authorization = getSharingToken?.();
  const headers: HeadersInit = authorization
    ? { Authorization: `Bearer ${authorization}` }
    : {};

  const response = await jsonFetch(`${baseURL}/api/v1/whiteboards`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      whiteboardable_type: objectType,
      whiteboardable_id: objectId,
      template_id: templateId,
      is_global: isGlobalTemplate,
      assistant_tool_call_id: llmToolCallId,
    }),
  });

  return response.json() as Promise<{ whiteboard: { id: RecordId } }>;
}

export function embeddedWhiteboardPlaceholder() {
  const tempId = String(Math.floor(Math.random() * 10000000));
  const placeholder: slate.Node = {
    type: 'embedded-whiteboard-placeholder',
    children: [{ text: '' }],
    placeholder_id: tempId,
  };

  return placeholder;
}

export function embeddedWhiteboard(whiteboardId) {
  return {
    type: 'embedded-whiteboard',
    children: [{ text: '' }],
    whiteboard_id: whiteboardId,
  };
}

interface EmbedWhiteboardParams {
  objectId?: string;
  objectType?: string;
  whiteboardId: RecordId;
  getSharingToken?: () => string;
  baseURL?: string;
}

interface EmbedWhiteboardResponse {
  whiteboard_id: RecordId;
}

export async function embedWhiteboard({
  objectId,
  objectType,
  whiteboardId,
  getSharingToken,
  baseURL,
}: EmbedWhiteboardParams) {
  const authorization = getSharingToken && getSharingToken();

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    'X-CSRF-Token': window.csrfToken(),
  };

  if (authorization) {
    headers.Authorization = `Bearer ${authorization}`;
  }

  const response = await fetch(
    `${baseURL ?? ''}/api/v1/whiteboards/${whiteboardId}/embed`,
    {
      method: 'POST',
      headers,
      body: JSON.stringify({
        whiteboardable_type: objectType,
        whiteboardable_id: objectId,
      }),
    }
  );

  return response
    .json()
    .then((res: EmbedWhiteboardResponse) => res.whiteboard_id);
}

export function insertWhiteboard(
  { editor }: ToolArgs,
  builder: Promise<RecordId>
): void {
  if (!editor) return;
  if (!editor.selection) return;

  const placeholder = embeddedWhiteboardPlaceholder();
  const placeholderId = placeholder.placeholder_id;

  builder
    .then(whiteboardId => {
      insertEmbeddedWhiteboard({
        editor,
        placeholderId: placeholderId,
        whiteboardId,
      });
    })
    .catch(err => {
      showAlertMessage(err);
      console.error(err);

      const placeholderNode = AhaEditorUtils.Editor.firstElementWithProperties(
        editor,
        {
          placeholder_id: placeholderId,
        }
      );

      if (placeholderNode) {
        slate.Transforms.removeNodes(editor, {
          at: placeholderNode[1],
        });
      }
    });

  slate.Transforms.insertNodes(editor, placeholder);
}
